import {
  compact,
  isArray,
  isEmpty,
  isPlainObject,
  isString,
  map,
  split,
  toInteger,
  trim,
} from 'lodash-es'

import { currency } from '@/utils/formatting'

const SECTION_KEY = {
  VIRTUAL_ACCOUNT: 'virtual_account',
  E_WALLET: 'e_wallet',
  OVER_THE_COUNTER: 'over_the_counter',
  QRIS: 'qris',
  CARD: 'card',
  PAYLATER: 'paylater',
  OTHERS: 'others',
}

const SECTION_META = {
  [SECTION_KEY.VIRTUAL_ACCOUNT]: {
    key: SECTION_KEY.VIRTUAL_ACCOUNT,
    titleKey: 'paymentConnect.section.virtual_account.title',
    subtitleKey: 'paymentConnect.section.virtual_account.subtitle',
  },
  [SECTION_KEY.E_WALLET]: {
    key: SECTION_KEY.E_WALLET,
    titleKey: 'paymentConnect.section.e_wallet.title',
    subtitleKey: 'paymentConnect.section.e_wallet.subtitle',
  },
  [SECTION_KEY.OVER_THE_COUNTER]: {
    key: SECTION_KEY.OVER_THE_COUNTER,
    titleKey: 'paymentConnect.section.over_the_counter.title',
    subtitleKey: 'paymentConnect.section.over_the_counter.subtitle',
  },
  [SECTION_KEY.QRIS]: {
    key: SECTION_KEY.QRIS,
    titleKey: 'paymentConnect.section.qris.title',
    subtitleKey: 'paymentConnect.section.qris.subtitle',
  },
  [SECTION_KEY.CARD]: {
    key: SECTION_KEY.CARD,
    titleKey: 'paymentConnect.section.card.title',
    subtitleKey: 'paymentConnect.section.card.subtitle',
  },
  [SECTION_KEY.PAYLATER]: {
    key: SECTION_KEY.PAYLATER,
    titleKey: 'paymentConnect.section.paylater.title',
    subtitleKey: 'paymentConnect.section.paylater.subtitle',
  },
  [SECTION_KEY.OTHERS]: {
    key: SECTION_KEY.OTHERS,
    titleKey: 'paymentConnect.section.others.title',
    subtitleKey: 'paymentConnect.section.others.subtitle',
  },
}

const SECTION_ORDER = [
  SECTION_KEY.VIRTUAL_ACCOUNT,
  SECTION_KEY.E_WALLET,
  SECTION_KEY.OVER_THE_COUNTER,
  SECTION_KEY.QRIS,
  SECTION_KEY.CARD,
  SECTION_KEY.PAYLATER,
  SECTION_KEY.OTHERS,
]

const METHOD_SECTION_BY_ID = {
  1: SECTION_KEY.CARD,
  2: SECTION_KEY.VIRTUAL_ACCOUNT,
  3: SECTION_KEY.VIRTUAL_ACCOUNT,
  4: SECTION_KEY.VIRTUAL_ACCOUNT,
  5: SECTION_KEY.VIRTUAL_ACCOUNT,
  6: SECTION_KEY.VIRTUAL_ACCOUNT,
  7: SECTION_KEY.E_WALLET,
  8: SECTION_KEY.E_WALLET,
  9: SECTION_KEY.QRIS,
  10: SECTION_KEY.OVER_THE_COUNTER,
  11: SECTION_KEY.OVER_THE_COUNTER,
  17: SECTION_KEY.PAYLATER,
  18: SECTION_KEY.PAYLATER,
  19: SECTION_KEY.VIRTUAL_ACCOUNT,
}

export const METHOD_LOGO_BY_ID = {
  1: '/resources/images/paymentProvider/mastercard.svg',
  2: '/resources/images/paymentProvider/bca.svg',
  3: '/resources/images/paymentProvider/bni.svg',
  4: '/resources/images/paymentProvider/bri.svg',
  5: '/resources/images/paymentProvider/mandiri.svg',
  6: '/resources/images/paymentProvider/permata.svg',
  7: '/resources/images/paymentProvider/gopay.svg',
  8: '/resources/images/paymentProvider/shopeepay.svg',
  9: '/resources/images/paymentProvider/qris.svg',
  10: '/resources/images/paymentProvider/alfamart.svg',
  11: '/resources/images/paymentProvider/indomaret.svg',
  17: '/resources/images/paymentProvider/akulaku.svg',
  18: '/resources/images/paymentProvider/kredivo.svg',
  19: '/resources/images/paymentProvider/cimb.svg',
}

const toLower = (val) => String(val || '').toLowerCase()

const extractPaymentMethodIds = (raw) => {
  if (isEmpty(raw)) {
    return []
  }
  if (isString(raw)) {
    return map(compact(map(split(raw, ','), trim)), toInteger)
  }
  if (isArray(raw)) {
    return map(raw, (item) => {
      if (isPlainObject(item)) {
        return toInteger(item.id)
      }
      return toInteger(item)
    })
  }
  return []
}

const detectSectionFromName = (name) => {
  const lowered = toLower(name)

  if (lowered.includes('virtual account') || lowered.includes('va ')) {
    return SECTION_KEY.VIRTUAL_ACCOUNT
  }
  if (
    lowered.includes('ovo') ||
    lowered.includes('gopay') ||
    lowered.includes('go-pay') ||
    lowered.includes('shopee') ||
    lowered.includes('dana') ||
    lowered.includes('e-wallet')
  ) {
    return SECTION_KEY.E_WALLET
  }
  if (lowered.includes('qris')) {
    return SECTION_KEY.QRIS
  }
  if (
    lowered.includes('alfamart') ||
    lowered.includes('indomaret') ||
    lowered.includes('alfamidi')
  ) {
    return SECTION_KEY.OVER_THE_COUNTER
  }
  if (lowered.includes('kartu') || lowered.includes('credit card') || lowered.includes('debit')) {
    return SECTION_KEY.CARD
  }
  if (lowered.includes('akulaku') || lowered.includes('kredivo') || lowered.includes('paylater')) {
    return SECTION_KEY.PAYLATER
  }
  return SECTION_KEY.OTHERS
}

export const getPaymentMethodSectionKey = (method = {}) => {
  const byId = METHOD_SECTION_BY_ID[toInteger(method.id)]
  if (byId) {
    return byId
  }
  return detectSectionFromName(method.name)
}

/** ID metode QRIS pertama di daftar available (Payment Connect / kasir). */
export const findKledoQrisMethodId = (availableMethods = []) => {
  if (!isArray(availableMethods) || !availableMethods.length) {
    return undefined
  }
  const found = availableMethods.find(
    (method) => getPaymentMethodSectionKey(method) === SECTION_KEY.QRIS,
  )
  return found != null && found.id != null ? toInteger(found.id) : undefined
}

export const isKledoCardPaymentMethod = (method = {}) => {
  return getPaymentMethodSectionKey(method) === SECTION_KEY.CARD
}

export const getActiveKledoPaymentMethods = (settingData = {}) => {
  const activeIds = extractPaymentMethodIds(settingData.kledo_active_payment_methods)
  const availableMethods = isArray(settingData.kledo_available_payment_methods)
    ? settingData.kledo_available_payment_methods
    : []

  if (!activeIds.length || !availableMethods.length) {
    return []
  }

  return availableMethods.filter((method) => activeIds.includes(toInteger(method.id)))
}

export const groupKledoPaymentMethods = (methods = []) => {
  const bucket = {}
  SECTION_ORDER.forEach((sectionKey) => {
    bucket[sectionKey] = []
  })

  methods.forEach((method) => {
    const sectionKey = getPaymentMethodSectionKey(method)
    bucket[sectionKey] = bucket[sectionKey] || []
    bucket[sectionKey].push(method)
  })

  return SECTION_ORDER.map((sectionKey) => ({
    ...SECTION_META[sectionKey],
    methods: bucket[sectionKey] || [],
  })).filter((section) => section.methods.length > 0)
}

export const flattenGroupedKledoMethods = (groupedSections = []) => {
  return groupedSections.reduce((acc, section) => {
    return acc.concat(section.methods || [])
  }, [])
}

/** Format biaya untuk tabel informasi: "Rp 4.000/trans · 2,50%" atau "Gratis" */
export const formatKledoChargeReadable = (charge) => {
  if (!charge || typeof charge !== 'object') {
    return 'Gratis'
  }

  const fixed = Number(charge.fixed)
  const percent = Number(charge.percent)
  const parts = []

  if (fixed > 0) {
    parts.push(`${currency(fixed)}/trans`)
  }

  if (percent > 0) {
    parts.push(`${(percent * 100).toFixed(2).replace('.', ',')}%`)
  }

  return parts.length ? parts.join(' · ') : 'Gratis'
}

export const formatKledoChargeLabel = (charge) => {
  if (!charge || typeof charge !== 'object') {
    return null
  }

  const fixed = Number(charge.fixed)
  const percent = Number(charge.percent)
  const labels = []
  if (fixed > 0) {
    labels.push(`+Rp${fixed.toLocaleString('id-ID')}`)
  }
  if (percent > 0) {
    labels.push(`+${(percent * 100).toFixed(2)}%`)
  }
  return labels.length ? labels.join(' ') : 'Tanpa biaya'
}
